import { useEffect, useState } from "react";

/**
 * Reactive view of the impersonation session marker the httpClient response
 * interceptor caches in localStorage under "impersonation_session_id". Returns
 * the session id (truthy while impersonating) or null otherwise.
 *
 * The localStorage marker alone is NOT sufficient: the backend attaches the
 * X-Impersonation-Session header to ANY session of the impersonated user/org,
 * so a real tenant user logging in while a super admin impersonates them would
 * also have the marker cached. The per-tab "impersonation_boot" sessionStorage
 * flag — set only by the ?ic= handoff in useImpersonationBoot — restricts
 * impersonation UI (banner, platform lock bypass) to the actual impersonation
 * tab.
 *
 * Picks up same-tab writes via a cheap 2s poll (the storage event does not fire
 * in the tab that wrote the value — the interceptor sets the flag after the
 * post-reload boot request resolves) and cross-tab writes via the storage
 * event. Shared by ImpersonationBanner and SidebarLayout so both render off the
 * same source of truth.
 */
export const useImpersonationSession = (): string | null => {
    const read = (): string | null =>
        sessionStorage.getItem("impersonation_boot")
            ? localStorage.getItem("impersonation_session_id")
            : null;

    const [sessionId, setSessionId] = useState<string | null>(read);

    useEffect(() => {
        const handler = () => setSessionId(read());

        window.addEventListener("storage", handler);
        const intervalId = window.setInterval(handler, 2000);

        return () => {
            window.removeEventListener("storage", handler);
            window.clearInterval(intervalId);
        };
    }, []);

    return sessionId;
};
